import { useRouteBodyState } from "@-/dev.improve.haniverse.lib.navigation.route-body-state";
import type { MorphemeInfoPanelAccordionSectionDescriptor } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.panels._components.panel-content._components.morpheme-info-panel-accordion";
import { Button } from "@-/dev.improve.haniverse.components.ui.button";
import { Icon } from "@-/dev.improve.haniverse.components.ui.icon";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import type { InfoMorphemeSlice } from "@-/dev.improve.haniverse.lib.morpheme.entities";
import { actorQueries } from "@-/dev.improve.haniverse.database.schemas.actor.query";
import { useUserCardActions } from "@-/dev.improve.haniverse.lib.cards.user-card.actions";
import { isUserCardMastered } from "@-/dev.improve.haniverse.lib.cards.user-card.mastery";
import { useQuery } from "@-/com.npmjs.tanstack__react-query";
import {
  useIsInsideLearnDeck,
  useLearnCardSurface,
} from "@-/dev.improve.haniverse.routes.nav.app.learn.deck.context";
import { useCallback, useMemo } from "react";
import { View } from "@-/com.npmjs.react-native";
import { LearnDeckSkipCardButton, SkipCardButton } from "#morpheme-info-panel-skip-card-button";

export function useMorphemeInfoPanelLearningProgressActionsSection({
  infoBackCardSlice,
}: {
  infoBackCardSlice: InfoMorphemeSlice;
}): MorphemeInfoPanelAccordionSectionDescriptor {
  const front =
    infoBackCardSlice.morphemeType === "grapheme"
      ? infoBackCardSlice.grapheme
      : infoBackCardSlice.word;
  const [isOpen, setIsOpen] = useRouteBodyState(
    "use-morpheme-info-panel-learning-progress-actions-section.tsx:isOpen",
    true,
  );
  const [isConfirmingReset, setIsConfirmingReset] = useRouteBodyState(
    "use-morpheme-info-panel-learning-progress-actions-section.tsx:isConfirmingReset",
    false,
  );
  const { markMasteredMutation, resetMutation, skipMutation, unmarkMasteredMutation } =
    useUserCardActions();
  // Inside a Learn deck, the current card's skip must go through the deck so the card advances;
  // a bare `skippedAt` write never leaves the review queue (or the lesson on show).
  const isInsideLearnDeck = useIsInsideLearnDeck();
  const { isCurrent } = useLearnCardSurface();
  const skipsThroughLearnDeck = isInsideLearnDeck && isCurrent;

  const { data: userCardData } = useQuery(
    actorQueries(import.meta.location).userGraphemeMeaningCard.findFirst.useQueryOptions({
      where: { grapheme: front },
      with: { userCard: { with: { fsrsCard: true } } },
    }),
  );
  const userCard = userCardData?.userCard ?? null;

  const isManuallyMastered = userCard?.manuallyMasteredAt != null;
  const isMastered =
    userCard !== null &&
    isUserCardMastered({
      lastReview: userCard.fsrsCard?.lastReview ?? null,
      manuallyMasteredAt: userCard.manuallyMasteredAt,
      scheduledDays: userCard.fsrsCard?.scheduledDays ?? null,
    });

  const status = useMemo(() => {
    if (userCard === null) {
      return "Not in your study yet.";
    }
    if (isManuallyMastered) {
      return "Marked as mastered.";
    }
    if (isMastered) {
      return "Mastered.";
    }
    if (userCard.fsrsCardId !== null) {
      return "In study.";
    }
    if (userCard.skippedAt !== null) {
      return "Skipped.";
    }
    return "Not in your study yet.";
  }, [userCard, isManuallyMastered, isMastered]);

  const isBusy =
    skipMutation.isPending ||
    resetMutation.isPending ||
    markMasteredMutation.isPending ||
    unmarkMasteredMutation.isPending;
  const isMasteryPending = markMasteredMutation.isPending || unmarkMasteredMutation.isPending;

  const handleSkip = useCallback(() => {
    setIsConfirmingReset(false);
    skipMutation.mutate({ front, kind: "morpheme" });
  }, [front, skipMutation, setIsConfirmingReset]);

  const handleToggleMastered = useCallback(() => {
    setIsConfirmingReset(false);
    if (isManuallyMastered) {
      unmarkMasteredMutation.mutate({ front, kind: "morpheme" });
      return;
    }
    markMasteredMutation.mutate({ front, kind: "morpheme" });
  }, [
    front,
    isManuallyMastered,
    markMasteredMutation,
    unmarkMasteredMutation,
    setIsConfirmingReset,
  ]);

  const handleReset = useCallback(() => {
    if (!isConfirmingReset) {
      setIsConfirmingReset(true);
      return;
    }
    setIsConfirmingReset(false);
    resetMutation.mutate({ front, kind: "morpheme" });
  }, [front, isConfirmingReset, resetMutation, setIsConfirmingReset]);

  return {
    bodyKey: "morpheme-info-learning-progress-actions-body",
    headerKey: "morpheme-info-learning-progress-actions-header",
    iconName: "LuPenLine",
    isOpen,
    onToggle: () => setIsOpen((previousState) => !previousState),
    renderBody: () => (
      <View className="gap-3 px-4 pt-3 pb-4">
        <Text className="text-xs text-muted-foreground">{status}</Text>

        {skipsThroughLearnDeck ? (
          <LearnDeckSkipCardButton
            disabled={isBusy}
            fallbackOnPress={handleSkip}
            front={front}
            isLoading={skipMutation.isPending}
          />
        ) : (
          <SkipCardButton
            disabled={isBusy}
            isLoading={skipMutation.isPending}
            onPress={handleSkip}
          />
        )}

        {isManuallyMastered ? (
          <Button
            disabled={isBusy}
            isLoading={isMasteryPending}
            onPress={handleToggleMastered}
            variant="outline"
          >
            <Icon className="text-foreground" name="LuRotateCcw" size={18} />
            <Text>Undo mastery</Text>
          </Button>
        ) : (
          <Button disabled={isBusy} isLoading={isMasteryPending} onPress={handleToggleMastered}>
            <Icon className="text-primary-foreground" name="LuTrophy" size={18} />
            <Text>Mark Known</Text>
          </Button>
        )}

        <Button
          disabled={isBusy}
          isLoading={resetMutation.isPending}
          onPress={handleReset}
          variant="destructive"
        >
          <Icon className="text-white" name="LuRotateCcw" size={18} />
          <Text>{isConfirmingReset ? "Tap again to confirm reset" : "Reset card progress"}</Text>
        </Button>

        {isConfirmingReset ? (
          <Text className="text-xs text-destructive">
            This deletes all SRS scheduling and review history for this card.
          </Text>
        ) : null}
      </View>
    ),
    title: "Actions",
  };
}
